import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import { useHudOverlay } from "widgets/hud/model/overlayStore";
import { AnimatedWrap } from "./styles";
import { useOutClick } from "app/lib/hooks/useOutClick";
import type { HudNavItem } from "widgets/hud/model/routes";
import { HudButton } from "widgets/hud/ui/HudButton/HudButton";
import { mergeRefs } from "shared/helpers";
import { useUserStore } from "entities/user";

type OtherModalProps = {
    items: HudNavItem[];
    anchorRef?: React.RefObject<HTMLElement | null>;
    offsetX?: number;
};

const OFFSET_X_DEFAULT = 30;
const VIEWPORT_PAD = 8;

export const OtherModal: React.FC<OtherModalProps> = ({ items, anchorRef, offsetX = OFFSET_X_DEFAULT }) => {
    const openId = useHudOverlay((s) => s.openId);
    const close = useHudOverlay((s) => s.close);
    const iAmAdmin = useUserStore(s => s.iAmAdmin);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const nodeRef = useRef<HTMLDivElement | null>(null);

    const [pos, setPos] = useState<{ top: number; left: number }>({ top: -99999, left: -99999 });
    const [ready, setReady] = useState(false);

    useOutClick(containerRef, close);

    const isOpen = openId === "other";

    const computePosition = () => {
        const el = nodeRef.current;
        const anchor = anchorRef?.current ?? null;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        if (!el) return;

        const rectEl = el.getBoundingClientRect();
        const width = rectEl.width || 320;
        const height = rectEl.height || 240;

        let top = VIEWPORT_PAD;
        let left = VIEWPORT_PAD;

        if (anchor) {
            const r = anchor.getBoundingClientRect();
            top = Math.max(VIEWPORT_PAD, Math.min(r.top, vh - height - VIEWPORT_PAD));
            left = Math.min(vw - width - VIEWPORT_PAD, r.right + (offsetX ?? OFFSET_X_DEFAULT));
        } else {
            top = Math.max(VIEWPORT_PAD, Math.min(vh / 2 - height / 2, vh - height - VIEWPORT_PAD));
            left = Math.max(VIEWPORT_PAD, Math.min(vw / 2 - width / 2, vw - width - VIEWPORT_PAD));
        }

        setPos({ top, left });
        setReady(true);
    };

    useLayoutEffect(() => {
        if (!isOpen) return;
        setReady(false);
        setPos({ top: -99999, left: -99999 });
        const id = requestAnimationFrame(() => {
            computePosition();
        });
        return () => cancelAnimationFrame(id);
    }, [isOpen, items.length, offsetX]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [isOpen, close]);

    useEffect(() => {
        if (!isOpen) return;
        const onRelayout = () => {
            setReady(false);
            requestAnimationFrame(() => computePosition());
        };
        window.addEventListener("resize", onRelayout);
        window.addEventListener("scroll", onRelayout, true);
        return () => {
            window.removeEventListener("resize", onRelayout);
            window.removeEventListener("scroll", onRelayout, true);
        };
    }, [isOpen, offsetX]);

    const style = useMemo<React.CSSProperties>(
        () => ({
            position: "fixed",
            top: pos.top,
            left: pos.left,
            maxHeight: "80vh",
            visibility: ready ? "visible" : "hidden",
        }),
        [pos.top, pos.left, ready]
    );

    return (
        <CSSTransition
            in={isOpen}
            timeout={{ enter: 200, exit: 150 }}
            classNames="hud-slide"
            unmountOnExit
            nodeRef={nodeRef}
        >
            <AnimatedWrap
                ref={mergeRefs(containerRef, nodeRef)}
                role="dialog"
                aria-modal="true"
                onMouseDown={(e) => e.stopPropagation()}
                style={style}
            >
                {items.map((item) => {
                  if (item.adminOnly&&!iAmAdmin) return <></>
                  
                  return (<HudButton key={item.id} item={item} aria-label={item.label} noCollapse />)
                })}
            </AnimatedWrap>
        </CSSTransition>
    );
};

export default OtherModal;
